import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Dropdown } from '../ui/Dropdown';
import { useToast } from '../../hooks/useToast';
import { app } from '../../lib/firebase';
import { BillingProfile, BillingoSettings } from '../../types/firestoreTypes';
import { BillingoDocumentBlock, BillingoBankAccount } from '../../types/billingoTypes';
import {
  BILLINGO_PAYMENT_METHOD,
  BILLINGO_LANGUAGE,
  BILLINGO_VAT,
  BILLINGO_UNIT_PRICE_TYPE,
  BILLINGO_DOCUMENT_BLOCK_TYPE,
} from '../../types/billingoConstants';

interface EditBillingProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  profileToEdit: BillingProfile | null;
}

// Helper function to generate translated options
const getTranslatedOptions = (
  t: Function,
  optionsObject: Record<string, string>,
  prefix: string,
  defaultLabelKey: string
): { value: string; label: string }[] => {
  return [
    { value: '', label: t(defaultLabelKey) },
    ...Object.entries(optionsObject).map(([key, value]) => ({
      value,
      label: t(`${prefix}.${key}`, key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())),
    })),
  ];
};

const EditBillingProfileModal: React.FC<EditBillingProfileModalProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
  profileToEdit,
}) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const functions = getFunctions(app, 'europe-west1');

  const [editingProfileData, setEditingProfileData] = useState<Partial<BillingProfile>>({});
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [documentBlocks, setDocumentBlocks] = useState<BillingoDocumentBlock[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BillingoBankAccount[]>([]);

  useEffect(() => {
    if (isOpen && profileToEdit) {
      setEditingProfileData({ ...profileToEdit });
      setApiKeyInput('');

      if (profileToEdit.isApiKeySet && profileToEdit.provider === 'billingo' && profileToEdit.profileId) {
        const fetchBillingoDetails = async () => {
          setIsLoadingDetails(true);
          setDocumentBlocks([]);
          setBankAccounts([]);

          try {
            const callListProviderDetails = httpsCallable(functions, 'listProviderDetails');
            const [blocksResult, accountsResult] = await Promise.all([
              callListProviderDetails({ profileId: profileToEdit.profileId, detailType: 'documentBlocks' }),
              callListProviderDetails({ profileId: profileToEdit.profileId, detailType: 'bankAccounts' }),
            ]);

            // Filter for invoice blocks and set state
            const blocks = blocksResult.data as BillingoDocumentBlock[];
            setDocumentBlocks(
              blocks.filter((block) => block.type === BILLINGO_DOCUMENT_BLOCK_TYPE.INVOICE)
            );

            // Set bank accounts
            setBankAccounts(accountsResult.data as BillingoBankAccount[]);
          } catch (error) {
            console.error('Error fetching Billingo details:', error);
            addToast({
              type: 'error',
              message: t('settings.billing.modals.edit.errors.loadDetailsFailed'),
            });
          } finally {
            setIsLoadingDetails(false);
          }
        };

        fetchBillingoDetails();
      } else {
        setIsLoadingDetails(false);
        setDocumentBlocks([]);
        setBankAccounts([]);
      }
    } else {
      setEditingProfileData({});
      setApiKeyInput('');
      setDocumentBlocks([]);
      setBankAccounts([]);
      setIsLoadingDetails(false);
    }
  }, [isOpen, profileToEdit, functions, t, addToast]);

  const handleProfileDataChange = (field: keyof Pick<BillingProfile, 'profileName'>, value: string) => {
    setEditingProfileData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSettingsChange = (field: keyof BillingoSettings, value: string) => {
    setEditingProfileData((prev) => {
      const newSettings = { ...(prev.settings || {}) };
      
      // Handle numeric fields
      if (['blockId', 'bankAccountId', 'dueDays'].includes(field)) {
        newSettings[field] = value === '' ? null : parseInt(value, 10);
      } else {
        newSettings[field] = value || null;
      }

      return {
        ...prev,
        settings: newSettings,
      };
    });
  };

  const handleSave = async () => {
    // Frontend validation
    if (!editingProfileData.profileName?.trim()) {
      addToast({
        type: 'error',
        message: t('settings.billing.modals.edit.validation.profileNameRequired')
      });
      return;
    }

    if (editingProfileData.provider === 'billingo') {
      if (!editingProfileData.settings?.defaultVat) {
        addToast({
          type: 'error',
          message: t('settings.billing.modals.edit.validation.defaultVatRequired')
        });
        return;
      }

      if (!editingProfileData.settings?.defaultUnitPriceType) {
        addToast({
          type: 'error',
          message: t('settings.billing.modals.edit.validation.unitPriceTypeRequired')
        });
        return;
      }
    }

    setIsSaving(true);

    try {
      const callSaveBillingProfile = httpsCallable(functions, 'saveBillingProfile');
      
      const payload: {
        profileId: string;
        profileName: string;
        provider: string;
        settings?: BillingoSettings;
        apiKey?: string;
      } = {
        profileId: editingProfileData.profileId!,
        profileName: editingProfileData.profileName,
        provider: editingProfileData.provider!,
        settings: editingProfileData.settings
      };

      if (apiKeyInput && apiKeyInput.trim() !== '') {
        payload.apiKey = apiKeyInput.trim();
      }

      await callSaveBillingProfile(payload);

      addToast({
        type: 'success',
        message: t('settings.billing.modals.edit.success.profileUpdated')
      });

      onSaveSuccess();
    } catch (error: any) {
      console.error('Error saving billing profile:', error);
      const errorMessage = error.message || t('settings.billing.modals.edit.errors.saveFailedDefault');
      
      addToast({
        type: 'error',
        message: t('settings.billing.modals.edit.errors.saveFailed', { error: errorMessage })
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const modalFooter = (
    <div className="flex justify-end gap-3">
      <Button
        variant="secondary"
        onClick={onClose}
        disabled={isSaving || isLoadingDetails}
      >
        {t('settings.billing.modals.edit.buttons.cancel')}
      </Button>
      <Button
        variant="primary"
        onClick={handleSave}
        isLoading={isSaving}
        disabled={isSaving || isLoadingDetails}
      >
        {t('settings.billing.modals.edit.buttons.save')}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('settings.billing.modals.edit.title', {
        profileName: editingProfileData.profileName || '',
      })}
      footer={modalFooter}
    >
      <div className="space-y-6">
        {isLoadingDetails ? (
          <div className="flex items-center justify-center p-4">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent"></div>
            <span className="ml-3 text-gray-600">
              {t('settings.billing.modals.edit.status.loading')}
            </span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Input
                label={t('settings.billing.modals.edit.form.profileName.label')}
                value={editingProfileData.profileName || ''}
                onChange={(e) => handleProfileDataChange('profileName', e.target.value)}
                required
              />
            </div>

            <div className="md:col-span-2">
              <Input
                label={t('settings.billing.modals.edit.form.apiKey.label')}
                type="password"
                placeholder={t('settings.billing.modals.edit.form.apiKey.placeholder')}
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
              />
            </div>

            <div className="md:col-span-2">
              <Input
                label={t('settings.billing.modals.edit.form.provider.label')}
                value={editingProfileData.provider || ''}
                readOnly
                disabled
                className="bg-gray-100"
              />
            </div>

            {editingProfileData.provider === 'billingo' && (
              <>
                <Dropdown
                  label={t('settings.billing.modals.edit.form.blockId.label')}
                  options={[
                    { value: '', label: t('settings.billingo.options.selectBlock') },
                    ...documentBlocks.map((block) => ({
                      value: block.id.toString(),
                      label: `${block.name} (${block.prefix})`,
                    })),
                  ]}
                  value={editingProfileData.settings?.blockId?.toString() || ''}
                  onChange={(value) => handleSettingsChange('blockId', value)}
                  disabled={documentBlocks.length === 0 || isLoadingDetails}
                />

                <Dropdown
                  label={t('settings.billing.modals.edit.form.bankAccountId.label')}
                  options={[
                    { value: '', label: t('settings.billingo.options.noneDefaultAccount') },
                    ...bankAccounts.map((account) => ({
                      value: account.id.toString(),
                      label: `${account.name} (...${account.account_number.slice(-4)})`,
                    })),
                  ]}
                  value={editingProfileData.settings?.bankAccountId?.toString() || ''}
                  onChange={(value) => handleSettingsChange('bankAccountId', value)}
                  disabled={bankAccounts.length === 0 || isLoadingDetails}
                />

                <Dropdown
                  label={t('settings.billingo.mainSettings.form.defaultVat.label')}
                  options={getTranslatedOptions(
                    t,
                    BILLINGO_VAT,
                    'settings.billingo.options.vat',
                    'settings.billingo.options.noneDefault'
                  )}
                  value={editingProfileData.settings?.defaultVat || ''}
                  onChange={(value) => handleSettingsChange('defaultVat', value)}
                />

                <Dropdown
                  label={t('settings.billingo.mainSettings.form.defaultUnitPriceType.label')}
                  options={getTranslatedOptions(
                    t,
                    BILLINGO_UNIT_PRICE_TYPE,
                    'settings.billingo.options.unitPriceType',
                    'settings.billingo.options.noneDefault'
                  )}
                  value={editingProfileData.settings?.defaultUnitPriceType || ''}
                  onChange={(value) => handleSettingsChange('defaultUnitPriceType', value)}
                />

                <Input
                  label={t('settings.billingo.mainSettings.form.defaultInvoiceItemUnit.label')}
                  placeholder={t('settings.billingo.mainSettings.form.defaultInvoiceItemUnit.placeholder')}
                  value={editingProfileData.settings?.defaultInvoiceItemUnit || ''}
                  onChange={(e) => handleSettingsChange('defaultInvoiceItemUnit', e.target.value)}
                />

                <Input
                  label={t('settings.billingo.mainSettings.form.customInvoiceItemName.label')}
                  placeholder={t('settings.billingo.mainSettings.form.customInvoiceItemName.placeholder')}
                  value={editingProfileData.settings?.customInvoiceItemName || ''}
                  onChange={(e) => handleSettingsChange('customInvoiceItemName', e.target.value)}
                />

                <Input
                  label={t('settings.billingo.mainSettings.form.dueDays.label')}
                  type="number"
                  min="0"
                  placeholder={t('settings.billingo.mainSettings.form.dueDays.placeholder')}
                  value={editingProfileData.settings?.dueDays?.toString() || ''}
                  onChange={(e) => handleSettingsChange('dueDays', e.target.value)}
                />

                <Dropdown
                  label={t('settings.billingo.mainSettings.form.defaultPaymentMethod.label')}
                  options={getTranslatedOptions(
                    t,
                    BILLINGO_PAYMENT_METHOD,
                    'settings.billingo.options.paymentMethod',
                    'settings.billingo.options.noneDefault'
                  )}
                  value={editingProfileData.settings?.defaultPaymentMethod || ''}
                  onChange={(value) => handleSettingsChange('defaultPaymentMethod', value)}
                />

                <Dropdown
                  label={t('settings.billingo.mainSettings.form.defaultLanguage.label')}
                  options={getTranslatedOptions(
                    t,
                    BILLINGO_LANGUAGE,
                    'settings.billingo.options.language',
                    'settings.billingo.options.noneDefault'
                  )}
                  value={editingProfileData.settings?.defaultLanguage || ''}
                  onChange={(value) => handleSettingsChange('defaultLanguage', value)}
                />
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EditBillingProfileModal;